import { forwardRef, useEffect, useState, useImperativeHandle } from 'react'
import { FormEditorMode, FormEditorRef, FormProps, SaveFormResponse } from './types'
import { DndContext, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Sidebar, { SidebarField } from './sidebar';
import Canvas, { Field } from './canvas';
import { FormProvider, useFormContext } from './context';
import { sortByParentChildRelationship } from './helpers';

type FormEditorProps = {
  onChange?: (form:SaveFormResponse) => void;
  onReady?: () => void;
  mode?: FormEditorMode;
}

const FormEditor = forwardRef<FormEditorRef, FormEditorProps>(function FormEditor({onChange, onReady, mode="edit"}, ref) {
  const { fields, fieldResponses, activeSidebarField, activeField, sidebarFieldsRegenKey, form_name, form_id, form_description, setform_name, setform_id, setform_description, handleDragStart, handleDragOver, handleDragEnd, loadInitialFields } = useFormContext();
  const [forceUpdate, setForceUpdate] = useState(0);

  const loadForm = (form:FormProps | SaveFormResponse) => {
    setform_name(form?.name || '')
    setform_description(form?.description || '')
    setform_id(form?.id || '')

    const sortedFields = form?.form?.fields?.map((field) => ({
      id: field?.id ?? '',
      type: field?.type ?? '',
      name: field?.name ?? '',
      question: field?.question ?? '',
      parent: field ? ('parent_id' in field ? field.parent_id : ('parent' in field ? field.parent : null)) : null,
      required: field ? ('required' in field ? field.required : false) : false,
      options: field?.options?.map((option) => ({
        id: option?.id,
        text: option?.text,
      })),
    }))

    loadInitialFields(sortByParentChildRelationship(sortedFields || []));
    setForceUpdate(forceUpdate + 1);
  };

  const saveForm = ():SaveFormResponse => {
    const newValue = { 
      id: form_id,
      name: form_name,
      description: form_description,
      form: {
        fields: fields,
        fieldResponses: fieldResponses
      }
    };
    
    return newValue
  };

  useImperativeHandle(ref, () => ({
    loadForm,
    saveForm,
  }));

  useEffect(()=>{
    if(onChange) onChange(saveForm())
  }, [fields, form_name, form_description])

  useEffect(()=>{
    if(onReady) onReady()
  }, [])

  return (
    <div className="flex w-full h-full overflow-auto">
      <DndContext
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        autoScroll
      >
        {mode === "edit" && <Sidebar fieldsRegKey={sidebarFieldsRegenKey} />}
        
        <SortableContext
          strategy={verticalListSortingStrategy}
          items={fields.map((f) => f.id)}
        >
          <Canvas fields={fields} mode={mode} key={forceUpdate}/>
        </SortableContext>
        
        <DragOverlay dropAnimation={undefined}>
          {activeSidebarField ? (
            <SidebarField field={activeSidebarField} />
          ) : null}
          {activeField ? <Field overlay field={activeField} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
})

const FormEditorProviders = forwardRef<FormEditorRef, FormEditorProps>(function Main(props, ref) {
  return (
    <FormProvider>
      <FormEditor ref={ref} {...props}/>
    </FormProvider>
  )
})

export default FormEditorProviders